import { useCallback, useState, useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI'
import CommentReqReportTable from './Table/CommentReqReportTable'
import { useQuery } from '@tanstack/react-query'
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import axios from 'axios'
import { useSelector } from 'react-redux'
import toasters from '../../../../../../../utils/toasters/Toasters'

const formatPayloadDates = (payload) => {
    if (!payload) return payload
    const formattedPayload = { ...payload }
    if (formattedPayload.startDate) {
        const startDate = new Date(formattedPayload.startDate)
        if (!isNaN(startDate))
            formattedPayload.startDate = startDate.toISOString().split('T')[0]
    }
    if (formattedPayload.endDate) {
        const endDate = new Date(formattedPayload.endDate)
        if (!isNaN(endDate))
            formattedPayload.endDate = endDate.toISOString().split('T')[0]
    }
    return formattedPayload
}

const CommonReqReport = () => {
    const location = useLocation()
    const { token } = useSelector((state) => state?.auth)

    const [searchParams] = useSearchParams();

  // Initialize payload from location.state, sessionStorage, or URL query params
  const [payload, setPayload] = useState(() => {
    if (location.state) return location.state;

    const stored = sessionStorage.getItem("commonReqPayload");
    if (stored) return JSON.parse(stored);

    const params = Object.fromEntries([...searchParams]);
    return Object.keys(params).length ? params : null;
  });

  const [formattedPayload, setFormattedPayload] = useState(() =>
    formatPayloadDates(payload)
  );

  const storedTableOptions =
    JSON.parse(sessionStorage.getItem("commonReqTableOptions")) || {
      rowPerPages: 10,
      activePage: 0,
    };
  const storedSort = JSON.parse(sessionStorage.getItem("commonReqSort")) || null;

  const [tableOptions, setTableOptions] = useState(storedTableOptions);
  const [sort, setSort] = useState(storedSort);

  // Sync sessionStorage for table options and sort
  useEffect(() => {
    sessionStorage.setItem(
      "commonReqTableOptions",
      JSON.stringify(tableOptions)
    );
  }, [tableOptions]);

  useEffect(() => {
    sessionStorage.setItem("commonReqSort", JSON.stringify(sort));
  }, [sort]);

  // Sync payload to sessionStorage when location.state or URL params change
  useEffect(() => {
    if (location.state) {
      const prevStored = sessionStorage.getItem("commonReqPayload");
      const prev = prevStored ? JSON.parse(prevStored) : null;

      if (JSON.stringify(prev) !== JSON.stringify(location.state)) {
        sessionStorage.setItem(
          "commonReqPayload",
          JSON.stringify(location.state)
        );
        setPayload(location.state);
        setFormattedPayload(formatPayloadDates(location.state));
      }
    } else if (!payload) {
      // fallback to URL params
      const params = Object.fromEntries([...searchParams]);
      if (Object.keys(params).length) {
        sessionStorage.setItem("commonReqPayload", JSON.stringify(params));
        setPayload(params);
        setFormattedPayload(formatPayloadDates(params));
      }
    }
  }, [location.state, searchParams]);

  const getCommonReqReport = useCallback(async () => {
    if (!formattedPayload) return [];

    let apiQueryParams = {
      per_page: tableOptions?.rowPerPages,
      page: tableOptions?.activePage + 1,
      export: "N",
    };

    if (sort?.columnName && sort.order) {
      apiQueryParams = {
        ...apiQueryParams,
        order: sort.order,
        sort: sort.columnName,
      };
    }

    return fetchIncidentAnalysisData({
      ...formattedPayload,
      ...apiQueryParams,
      export: "N",
    });
  }, [formattedPayload, tableOptions, sort]);

  const { isLoading, isFetching, data, isError, error } = useQuery(
    ["fetch-common-req-report", tableOptions, sort, formattedPayload],
    getCommonReqReport,
    {
      enabled: !!formattedPayload, 
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        return failureCount < 1 && status >= 400 && status < 600;
      },
      retryDelay: 1000,
    }
  );

    const handlePageClick = (event) =>
        setTableOptions((prev) => ({ ...prev, activePage: event.selected }))
    const handleRowsPerPage = (e) =>
        setTableOptions((prev) => ({
            ...prev,
            rowPerPages: e.target.value,
            activePage: 0,
        }))

    const handleColumnSort = (column, sortDirection) =>
        setSort({ columnName: column?.sortField, order: sortDirection })

 const downloadPDF = async () => {
    try {
        const payloadWithExport = {
            ...formattedPayload,
            export: 'N',
            pagination: 'false',
        }

        const response = await axios.post(
            `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
            payloadWithExport,
            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            }
        )

        const fullData = response?.data?.data || []
        if (!Array.isArray(fullData) || fullData.length === 0) {
            toasters({ type: 'error', message: 'No data found for PDF' })
            return
        }

        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: [297, 210],
        })

        // Title
        doc.setFontSize(18)
        doc.text('Comment Request Report', 14, 22)

        // Date Range
        doc.setFontSize(12)
        doc.text(
            `Date Range: ${formattedPayload.startDate} - ${formattedPayload.endDate}`,
            14,
            30
        )

        const tableColumn = [
            'User ID',
            'WMRC Name',
            'Comment Request Count',
        ]
        const tableRows = fullData.map((item) => [
            item?.user_id || '',
            item?.wmrc_name || '',
            item?.comment_request_count || 0,
        ])

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 40,
            theme: 'grid',
            // 1. Align table edges with text margins
            margin: { left: 14, right: 14 },
            // 2. Force the table to expand to the margins
            tableWidth: 'auto', 
            headStyles: {
                fillColor: [0, 81, 242],
                textColor: [255, 255, 255],
                fontSize: 10,
                halign: 'center'
            },
            bodyStyles: { 
                fontSize: 10, 
                cellPadding: 4, 
                halign: 'left' 
            },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            styles: { 
                lineColor: [180, 180, 180], 
                lineWidth: 0.2,
                overflow: 'linebreak' 
            },
            // 3. Remove fixed numbers to distribute the 269mm of available space
            columnStyles: {
                0: { cellWidth: 'auto' }, 
                1: { cellWidth: 'auto', halign: 'left' }, // Name usually needs more space
                2: { cellWidth: 'auto' },
            },
        })

        // Totals calculation
        const totalUsers = fullData.length
        const totalCommentRequests = fullData.reduce(
            (sum, item) => sum + (item.comment_request_count || 0),
            0
        )

        const finalY = doc.lastAutoTable.finalY + 10
        doc.setFontSize(12)
        doc.text(`Total Users: ${totalUsers}`, 14, finalY)
        doc.text(
            `Total Comment Requests: ${totalCommentRequests}`,
            14,
            finalY + 6
        )

        doc.save('comment-request-report.pdf')
    } catch (error) {
        console.error('PDF Export Error:', error)
        toasters({ type: 'error', message: 'Error generating PDF' })
    }
}

    const downloadFile = (csvString, fileName = 'download') => {
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${fileName}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const ExcelCommonReqReport = async () => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                {
                    ...formattedPayload,
                    export: 'Y',
                    pagination: 'false',
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            )

            if (!response?.data || typeof response.data !== 'string') {
                toasters({ type: 'error', message: 'No data found to export' })
                return
            }

            downloadFile(response.data, 'comment-request-report')
        } catch (error) {
            toasters({
                type: 'error',
                message:
                    error?.response?.data?.message ||
                    'Error downloading report',
            })
        }
    }

    const pageCount = Math.ceil((data?.total || 0) / tableOptions.rowPerPages)

    return (
        <>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (
                <CommentReqReportTable
                    isLoading={isLoading || isFetching}
                    data={data?.data || []}
                    dates={formattedPayload}
                    totaldata={data?.total}
                    tableOptions={tableOptions}
                    handlePageClick={handlePageClick}
                    handleRowsPerPage={handleRowsPerPage}
                    handleColumnSort={handleColumnSort}
                    downloadPDF={downloadPDF}
                    downloadExcel={ExcelCommonReqReport}
                    pageCount={pageCount}
                    reportData={formattedPayload}
                    reportCode={payload?.code}
                    reportTableData={data?.data}
                    generatePDFFunction={downloadPDF}
                />
            )}
        </>
    )
}

export default CommonReqReport
